import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { useOrganization } from "@/hooks/useOrganization";
import { sendsReceiptOnReview } from "@/lib/receipt-on-review";
import { toast } from "sonner";
import { showUndoToast, showSuccessToast, showErrorToast } from "@/lib/undo-toast";
import { parseLocalDate } from "@/lib/date-utils";
import { resolveDateRange, toDateRangeYmdBounds } from "@/lib/filters/date-range";
import { queryKeys } from "@/lib/query-keys";
import { trackQueryPerformance } from "@/lib/performance";
import { buildFinanceBaseEvent, emitFinanceEvent } from "@/lib/finance-instrumentation/events";
import {
  autoPostContributionsByContributionIds,
  deleteContributionsWithReversal,
  undeleteContributionsWithPostings,
} from "@/api/finance-queue";
import { generateReceiptEmail } from "@/lib/email-templates/contribution-receipt";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { logAudit } from "@/lib/audit";
import { summarizeContributionAllocations } from "@/lib/contribution-allocation-display";
import {
  isCountableGivingContribution,
  isPaymentProcessorContributionSource,
  isSettledStripeContribution,
} from "@/lib/contribution-settlement";
import { format } from "date-fns";

const CONTRIBUTION_BULK_DELETE_CHUNK_SIZE = 100;

// Types
export interface Allocation {
  id: string;
  fund_id: string;
  amount: number;
  campus?: string | null;
  program?: string | null;
  contribution_id?: string;
  created_at?: string;
  /** Non-deductible portion (quid pro quo fair market value). */
  non_deductible_amount?: number | null;
  fund?: {
    id: string;
    name: string;
    type: string;
    is_tax_deductible?: boolean;
  };
}

export interface Contribution {
  id: string;
  person_id: string | null;
  amount: number;
  refunded_amount?: number;
  date: string;
  method: string;
  status: string;
  deleted_at?: string | null;
  void_reason?: string | null;
  check_number?: string | null;
  transaction_id?: string | null;
  qbo_entity_id?: string | null;
  pledge_id?: string | null;
  notes?: string | null;
  deposit_id?: string | null;
  batch_id?: string | null;
  batch_date?: string | null;
  batch_source?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  organization_id: string;
}

export interface ContributionWithDetails extends Contribution {
  person?: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string | null;
    photo_url?: string | null;
    household_id?: string | null;
  };
  allocations?: Allocation[];
}

export interface CreateContributionInput {
  person_id: string | null;
  amount: number;
  date: string;
  method: string;
  status?: string;
  check_number?: string;
  notes?: string;
  batch_id?: string;
  batch_date?: string;
  batch_source?: string;
  donor_name?: string; // For anonymous/guest donors - stored in notes if no person_id
  allocations: {
    fund_id: string;
    amount: number;
    campus?: string;
    program?: string;
    pledge_id?: string | null;
  }[];
}

export interface ContributionsQueryOptions {
  /** Keep related-record reads separate for a verified viewer/access revision. */
  cacheScope?: string;
  /**
   * A canonical date-range preset key (`@/lib/filters/date-range`). Legacy keys
   * such as `month` still resolve, but now to the window their label always
   * claimed — `month` used to run a trailing 30-day query behind a "This Month"
   * label, and is normalized to `month_to_date`.
   */
  dateRange?: string;
  /** `YYYY-MM-DD`, read only when `dateRange` is the `custom` preset. */
  customStartDate?: string;
  /** `YYYY-MM-DD`, read only when `dateRange` is the `custom` preset. */
  customEndDate?: string;
  /**
   * 1-12. Required for the fiscal presets to mean anything: without it the
   * resolver assumes January, so a July-fiscal org would see a fiscal label on
   * the filter while this query returned calendar-year rows.
   */
  fiscalYearStartMonth?: number;
  /**
   * Extra cache-key fragment for a caller-resolved window. Screens driven by
   * the shared date filter pass `dateFilter.query.key`, which covers the
   * fiscal calendar and the current day too — so a midnight rollover or a
   * late fiscal month invalidates instead of serving the old window.
   */
  rangeCacheKey?: string;
  /** Explicit start date as ISO string (YYYY-MM-DD). Takes priority over dateRange for report adapters. */
  startDate?: string;
  /** Explicit end date as ISO string (YYYY-MM-DD). Takes priority over dateRange for report adapters. */
  endDate?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
  mode?: "all" | "page";
  /** Fetch every matching row in deterministic 1,000-row pages (for report totals). */
  fetchAllPages?: boolean;
  search?: string;
  sort?: string;
}

export type ContributionsPageParams = Omit<ContributionsQueryOptions, "mode">;

export interface PaginatedContributions {
  data: ContributionWithDetails[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

type ContributionQuerySortDirection = "asc" | "desc";
type ContributionQuerySortKey = "date" | "contributor" | "amount" | "method" | "status";

function parseContributionQuerySort(sort: string | undefined): {
  key: ContributionQuerySortKey;
  direction: ContributionQuerySortDirection;
} | null {
  if (!sort) return null;

  const [key, direction] = sort.split(":");
  if (direction !== "asc" && direction !== "desc") return null;

  switch (key) {
    case "date":
    case "contributor":
    case "amount":
    case "method":
    case "status":
      return { key, direction };
    default:
      return null;
  }
}

function toMinorUnits(amount: number): number {
  if (!Number.isFinite(amount)) return NaN;

  const isNegative = amount < 0;
  const normalized = Math.abs(amount).toString().includes("e")
    ? Math.abs(amount).toLocaleString("en-US", {
        useGrouping: false,
        maximumFractionDigits: 20,
      })
    : Math.abs(amount).toString();
  const [wholePart, fractionPart = ""] = normalized.split(".");
  const fraction = fractionPart.padEnd(3, "0");
  const wholeMinor = Number.parseInt(wholePart || "0", 10) * 100;
  const centMinor = Number.parseInt(fraction.slice(0, 2), 10);
  const roundedMinor = wholeMinor + centMinor + (Number(fraction[2] ?? "0") >= 5 ? 1 : 0);

  return isNegative ? -roundedMinor : roundedMinor;
}

/**
 * Validates that the allocation amounts sum to the contribution amount.
 * Compares in minor units (cents) to avoid IEEE-754 drift on values like
 * 0.1 + 0.2. Returns null if valid, or an error message describing the drift.
 *
 * Sub-cent drift is real audit risk per IRC §170: an allocation total of
 * $100.005 against a $100.00 receipt invites a CP-2000 letter. Reject at
 * the hook layer so the DB never sees mismatched rows.
 */
function validateAllocationTotal(
  contributionAmount: number,
  allocations: { amount: number }[],
): string | null {
  if (!Number.isFinite(contributionAmount)) {
    return "Contribution amount must be a finite number";
  }
  if (allocations.length === 0) return null; // unallocated is allowed
  const totalMinor = allocations.reduce((sum, alloc) => {
    const amt = Number(alloc.amount);
    if (!Number.isFinite(amt)) return NaN;
    return sum + toMinorUnits(amt);
  }, 0);
  if (!Number.isFinite(totalMinor)) {
    return "Allocation amounts must all be finite numbers";
  }
  const contribMinor = toMinorUnits(contributionAmount);
  if (totalMinor !== contribMinor) {
    return `Allocation total (${(totalMinor / 100).toFixed(2)}) must equal contribution amount (${(contribMinor / 100).toFixed(2)})`;
  }
  return null;
}

export interface ContributionAllocationReplacement {
  id?: string;
  fund_id: string;
  amount: number;
}

type ContributionEditAccountingFacts = {
  amount: number;
  date: string;
  method: string;
  check_number?: string | null;
};

function allocationIdentity(
  allocation: Pick<ContributionAllocationReplacement, "id" | "fund_id" | "amount">,
): string {
  return `${allocation.id ?? ""}\u0000${allocation.fund_id}\u0000${allocation.amount}`;
}

export function contributionEditNeedsAllocationReplacement(
  current: ContributionEditAccountingFacts,
  next: ContributionEditAccountingFacts,
  currentAllocations: readonly ContributionAllocationReplacement[],
  nextAllocations: readonly ContributionAllocationReplacement[],
): boolean {
  if (
    current.amount !== next.amount ||
    current.date !== next.date ||
    current.method.trim().toLowerCase() !== next.method.trim().toLowerCase() ||
    normalizeContributionCheckNumber(current.method, current.check_number) !==
      normalizeContributionCheckNumber(next.method, next.check_number)
  ) {
    return true;
  }

  if (currentAllocations.length !== nextAllocations.length) return true;
  const currentIdentities = currentAllocations.map(allocationIdentity).sort();
  const nextIdentities = nextAllocations.map(allocationIdentity).sort();
  return currentIdentities.some((identity, index) => identity !== nextIdentities[index]);
}

export function validateContributionAllocationReplacement(
  contributionAmount: number,
  allocations: readonly ContributionAllocationReplacement[],
): string | null {
  if (!Number.isFinite(contributionAmount)) {
    return "Contribution amount must be a finite number";
  }
  if (allocations.length === 0) return null;
  for (const allocation of allocations) {
    if (!allocation.fund_id.trim()) return "Every allocation requires a fund";
    if (!Number.isFinite(allocation.amount) || allocation.amount <= 0) {
      return "Allocation amounts must be positive numbers";
    }
    if (Math.abs(allocation.amount * 100 - Math.round(allocation.amount * 100)) > 1e-7) {
      return "Allocation amounts cannot use fractions of a cent";
    }
  }
  const allocationTotalMinor = allocations.reduce(
    (sum, allocation) => sum + toMinorUnits(allocation.amount),
    0,
  );
  const contributionMinor = toMinorUnits(contributionAmount);
  if (allocationTotalMinor > contributionMinor) {
    return `Allocation total (${(allocationTotalMinor / 100).toFixed(2)}) cannot exceed contribution amount (${(contributionMinor / 100).toFixed(2)})`;
  }
  return null;
}

function normalizeContributionCheckNumber(
  method: string | null | undefined,
  checkNumber: string | null | undefined,
): string | null {
  if (method?.trim().toLowerCase() !== "check") return null;
  return checkNumber?.trim() || null;
}

function validateContributionCheckNumber(
  method: string | null | undefined,
  checkNumber: string | null | undefined,
): string | null {
  if (method?.trim().toLowerCase() === "check" && !normalizeContributionCheckNumber(method, checkNumber)) {
    return "Check contributions require a check number";
  }
  return null;
}

function resolveFinanceEnvironment(): "prod" | "staging" | "dev" {
  const mode = import.meta.env.MODE;
  if (mode === "production") return "prod";
  if (mode === "staging") return "staging";
  return "dev";
}

async function emitContributionPaymentEvent(params: {
  eventName:
    | "finance.payment_intent.created"
    | "finance.payment_intent.confirmed"
    | "finance.payment.captured"
    | "finance.payment.settled"
    | "finance.payment.failed";
  organizationId: string;
  contributionId: string;
  amount: number;
  method: string;
  transactionId?: string | null;
  userId?: string | null;
  failureReason?: string | null;
  retryAttempt?: number;
}) {
  const base = buildFinanceBaseEvent(
    params.eventName,
    {
      accountId: params.organizationId,
      userId: params.userId ?? null,
      sourceSystem: "web_app",
      environment: resolveFinanceEnvironment(),
    },
    toMinorUnits(params.amount),
    "USD",
  );
  await emitFinanceEvent({
    ...base,
    payment_intent_id: params.contributionId,
    payment_id: params.transactionId ?? params.contributionId,
    payment_method_type: params.method === "card" || params.method === "credit_card"
      ? "card" : params.method === "ach" ? "bank_transfer" : "other",
    processor: "forgehq",
    processor_response_code: null,
    failure_reason: params.failureReason ?? null,
    is_retry: (params.retryAttempt ?? 0) > 0,
    retry_attempt: params.retryAttempt ?? 0,
  });
}

async function emitContributionPaymentEventBestEffort(
  params: Parameters<typeof emitContributionPaymentEvent>[0],
): Promise<void> {
  try {
    await emitContributionPaymentEvent(params);
  } catch (eventError) {
    console.warn(
      "Contribution finance event emission failed after the database write committed (non-blocking):",
      eventError,
    );
  }
}

async function emitBatchContributionPaymentEventsBestEffort(params: {
  organizationId: string;
  contributions: Contribution[];
  captureReceivedCashAndChecks?: boolean;
}): Promise<void> {
  try {
    const eventResults = await Promise.allSettled(
      params.contributions.flatMap((created) => {
        const events: Parameters<typeof emitContributionPaymentEvent>[0]["eventName"][] = ["finance.payment_intent.created"];
        if (params.captureReceivedCashAndChecks
          && ["cash", "check"].includes(created.method)
          && (created.status ?? "").trim().toLowerCase() === "completed"
          && !isPaymentProcessorContributionSource(created.batch_source)
          && isCountableGivingContribution(created.status, created.batch_source)) {
          events.push("finance.payment.captured");
        }
        return events.map((eventName) => emitContributionPaymentEvent({
          eventName,
          organizationId: params.organizationId,
          contributionId: created.id,
          amount: created.amount,
          method: created.method,
          transactionId: created.transaction_id,
          // The RPC has committed. A later account switch or logout cannot
          // change who created this row; missing provenance stays unattributed.
          userId: created.created_by ?? null,
        }));
      })
    );
    const failedEvents = eventResults.filter(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );
    if (failedEvents.length > 0) {
      console.warn(
        `Batch contribution finance event emission failed for ${failedEvents.length} event${failedEvents.length === 1 ? "" : "s"} (non-blocking):`,
        failedEvents.map((result) => result.reason)
      );
    }
  } catch (eventError) {
    console.warn(
      "Batch contribution finance event emission failed (non-blocking):",
      eventError
    );
  }
}

// Helper to extract array from useContributions result
export function getContributionsArray(
  result: ContributionWithDetails[] | PaginatedContributions | undefined
): ContributionWithDetails[] {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  return result.data;
}

// Type guard to check if result is paginated
export function isPaginatedContributions(
  result: ContributionWithDetails[] | PaginatedContributions | undefined
): result is PaginatedContributions {
  return !!result && !Array.isArray(result) && "data" in result;
}

// Fetch all contributions with details (with optional pagination)
export function useContributions(options?: ContributionsQueryOptions) {
  const { organizationId } = useOrganization();
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 100;
  const mode = options?.mode ?? (options?.page !== undefined || options?.pageSize !== undefined ? "page" : "all");
  const usePagination = mode === "page";
  const safePage = Math.max(1, page);
  const safePageSize = Math.max(1, pageSize);
  const filters = {
    dateRange: options?.dateRange,
    customStartDate: options?.customStartDate,
    customEndDate: options?.customEndDate,
    fiscalYearStartMonth: options?.fiscalYearStartMonth,
    rangeCacheKey: options?.rangeCacheKey,
    startDate: options?.startDate,
    endDate: options?.endDate,
    search: options?.search?.trim() || undefined,
    sort: options?.sort?.trim() || undefined,
    fetchAllPages: options?.fetchAllPages === true || undefined,
  };
  const queryFilters =
    filters.search ||
    filters.sort ||
    filters.dateRange ||
    filters.startDate ||
    filters.endDate ||
    filters.rangeCacheKey
      ? filters
      : undefined;

  const baseQueryKey = usePagination
    ? queryKeys.contributions.page(organizationId, options?.dateRange, safePage, safePageSize, queryFilters)
    : queryKeys.contributions.list(organizationId, queryFilters);
  const queryKey = options?.cacheScope === undefined ? baseQueryKey : [...baseQueryKey, { cacheScope: options.cacheScope }];
  const queryName = usePagination ? `contributions-page-${safePage}-${safePageSize}` : "contributions-all";

  return useQuery({
    queryKey,
    queryFn: trackQueryPerformance(queryName, async () => {
      if (!organizationId) return usePagination
        ? { data: [], totalCount: 0, page: safePage, pageSize: safePageSize, totalPages: 0 }
        : [];

      const sort = parseContributionQuerySort(filters.sort);
      const isAscending = sort?.direction === "asc";
      let query = supabase
        .from("contributions")
        .select(
          `
            *,
            person:people!contributions_person_org_fkey(id, first_name, last_name, email, photo_url, household_id),
            allocations(id, fund_id, amount, campus, program, non_deductible_amount, fund:funds!allocations_fund_org_fkey(id, name, type, is_tax_deductible))
          `,
          { count: usePagination ? "exact" : undefined }
        )
        .eq("organization_id", organizationId)
        // Exclude soft-deleted rows. The `contributions` table has a
        // `deleted_at` column; without this filter every report adapter,
        // dashboard widget, and totals tile silently counts deleted gifts.
        .is("deleted_at", null);
      if (sort?.key === "contributor") {
        query = query
          .order("person(first_name)", { ascending: isAscending, nullsFirst: false })
          .order("person(last_name)", { ascending: isAscending, nullsFirst: false })
          .order("date", { ascending: false })
          .order("created_at", { ascending: false })
          .order("id", { ascending: false });
      } else if (sort?.key === "date") {
        query = query
          .order("date", { ascending: isAscending, nullsFirst: false })
          .order("created_at", { ascending: isAscending, nullsFirst: false })
          .order("id", { ascending: isAscending, nullsFirst: false });
      } else if (sort) {
        query = query
          .order(sort.key, { ascending: isAscending, nullsFirst: false })
          .order("date", { ascending: false })
          .order("created_at", { ascending: false })
          .order("id", { ascending: false });
      } else {
        query = query
          .order("date", { ascending: false })
          .order("created_at", { ascending: false })
          .order("id", { ascending: false }); // tiebreaker for deterministic ordering when (date, created_at) collide
      }

      // Apply the preset window. Unlike the previous helper this bounds both
      // ends, so "Last month" and a custom range exclude anything after the
      // period instead of running open-ended from its start date.
      if (options?.dateRange) {
        const presetBounds = toDateRangeYmdBounds(
          resolveDateRange(options.dateRange, {
            customStart: options.customStartDate,
            customEnd: options.customEndDate,
            fiscalYearStartMonth: options.fiscalYearStartMonth,
          }),
        );
        if (presetBounds) {
          query = query
            .gte("date", presetBounds.startDate)
            .lte("date", presetBounds.endDate);
        }
      }

      // Apply explicit start/end date filters (used by report adapters)
      if (options?.startDate) {
        query = query.gte("date", options.startDate);
      }
      if (options?.endDate) {
        query = query.lte("date", options.endDate);
      }

      // Apply pagination if specified
      if (usePagination) {
        const from = (safePage - 1) * safePageSize;
        const to = from + safePageSize - 1;
        query = query.range(from, to);
      }

      if (!usePagination && options?.fetchAllPages === true) {
        const PAGE_SIZE = 1000;
        const rows: ContributionWithDetails[] = [];
        for (let from = 0; ; from += PAGE_SIZE) {
          const { data, error } = await query.range(from, from + PAGE_SIZE - 1);
          if (error) throw error;
          const page = (data ?? []) as ContributionWithDetails[];
          rows.push(...page);
          if (page.length < PAGE_SIZE) break;
        }
        return rows;
      }

      const { data, error, count } = await query;

      if (error) throw error;

      if (usePagination) {
        const totalCount = count ?? 0;
        return {
          data: data as ContributionWithDetails[],
          totalCount,
          page: safePage,
          pageSize: safePageSize,
          // Use the clamped `safePageSize` (>=1) to avoid Infinity when a
          // caller passes pageSize=0 (NaN/Infinity propagation pattern).
          totalPages: Math.max(0, Math.ceil(totalCount / safePageSize)),
        };
      }

      return data as ContributionWithDetails[];
    }),
    enabled: options?.enabled !== false && !!organizationId,
  });
}

export function useContributionsPage(options?: ContributionsPageParams) {
  return useContributions({ ...options, mode: "page" });
}

// Fetch single contribution
export function useContribution(id: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    // Include organizationId in the cache key. Without it, switching
    // organizations (e.g. multi-org admins) can serve another tenant's
    // contribution from the cache before the queryFn refetches.
    queryKey: ["contribution", organizationId, id],
    queryFn: async () => {
      if (!id || !organizationId) return null;

      const { data, error } = await supabase
        .from("contributions")
        .select(`
          *,
          person:people!contributions_person_org_fkey(id, first_name, last_name, email, phone, photo_url),
          allocations(id, fund_id, amount, campus, program, non_deductible_amount, fund:funds!allocations_fund_org_fkey(id, name, type, is_tax_deductible))
        `)
        .eq("id", id)
        .eq("organization_id", organizationId)
        // Defense in depth: skip soft-deleted rows.
        .is("deleted_at", null)
        .maybeSingle();

      if (error) throw error;
      return (data ?? null) as ContributionWithDetails | null;
    },
    enabled: !!id && !!organizationId,
  });
}

/**
 * Best-effort donor receipt (IRC §170(f)(8) — written acknowledgment required
 * for gifts ≥$250; we send for all gifts as best practice). A staff-entered
 * pending record is explicitly not a recognized gift, so nothing is sent at
 * creation until it is countable. At review one is sent only when the
 * organization turned that on (#6479, useMarkStaffContributionReviewed);
 * otherwise staff use "Send Receipt".
 *
 * send-receipt records the receipt on the gift (contributions.receipt_sent_at,
 * #6481) and claims it before sending, so this automatic send never forces: a
 * retry, or a gift that already has a receipt recorded, sends nothing more.
 * Never throws: receipt delivery must not fail the save or review.
 */
async function sendContributionReceiptBestEffort(params: {
  organizationId: string;
  contribution: Pick<Contribution, "id" | "person_id" | "amount" | "date" | "status" | "batch_source">;
  allocations: ReadonlyArray<{ fund_id: string; amount: number }>;
}): Promise<void> {
  const { organizationId, contribution: created, allocations } = params;
  try {
    const personId = created.person_id;
    if (!personId || !(created.amount > 0) || !isCountableGivingContribution(created.status, created.batch_source)) {
      return;
    }
    const { data: personRow } = await supabase
      .from("people")
      .select("first_name, last_name, email")
      .eq("id", personId)
      .eq("organization_id", organizationId)
      .maybeSingle();

    const person = personRow as { first_name: string | null; last_name: string | null; email: string | null } | null;
    if (!person?.email) return;

    let fundName = "Unallocated";
    const allocationFundIds = [...new Set(allocations.map((allocation) => allocation.fund_id))];
    if (allocationFundIds.length > 0) {
      const { data: fundRows, error: fundError } = await supabase
        .from("funds")
        .select("id, name")
        .in("id", allocationFundIds)
        .eq("organization_id", organizationId)
        .order("name");
      if (fundError) {
        console.warn("Receipt fund-label lookup failed (non-blocking):", fundError);
      } else {
        const fundNamesById = new Map(
          ((fundRows ?? []) as Array<{ id: string; name: string | null }>).map((fund) => [
            fund.id,
            fund.name,
          ]),
        );
        fundName = summarizeContributionAllocations(
          allocations.map((allocation) => ({
            amount: allocation.amount,
            fund: { name: fundNamesById.get(allocation.fund_id) ?? null },
          })),
          {
            emptyLabel: "Unallocated",
            fallbackFundName: "Unknown fund",
          },
        );
      }
    }

    const { data: orgRow } = await supabase
      .from("organizations")
      .select("name, slug, ein, settings")
      .eq("id", organizationId)
      .maybeSingle();

    const org = orgRow as { name: string | null; slug: string | null; ein: string | null; settings: unknown } | null;
    const churchName = org?.name ?? org?.slug ?? "";
    // EIN is canonical in the organizations.ein column; fall back to the
    // legacy settings JSONB only for orgs that set it there historically.
    const orgSettings = org?.settings as { ein?: string } | null;
    const churchEin = org?.ein ?? orgSettings?.ein ?? "Not provided";
    const donorName = `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim();
    const dateLabel = format((parseLocalDate(created.date) ?? new Date(created.date)), "MMMM d, yyyy");

    await invokeEdgeFunction("send-receipt", {
      body: {
        to: person.email,
        contributionId: created.id,
        subject: churchName ? `Contribution Receipt - ${churchName}` : "Contribution Receipt",
        html: generateReceiptEmail({
          recipientName: donorName || "Friend",
          amount: created.amount,
          date: dateLabel,
          fundName,
          churchName,
          churchEin,
          contributionId: created.id,
        }),
      },
      policy: "interactive",
      timeoutMs: 12_000,
      idempotencyKey: `send-receipt:contribution:${created.id}`,
    });
  } catch (receiptError) {
    // Non-blocking: receipt delivery failure should never block the save or review.
    console.warn("Auto-send receipt failed (non-blocking):", receiptError);
  }
}

// Create contribution with allocations
export function useCreateContribution() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (input: CreateContributionInput) => {
      if (!organizationId) throw new Error("No organization selected");
      if (!Number.isFinite(input.amount)) {
        throw new Error("Contribution amount must be a finite number");
      }
      const allocationError = validateAllocationTotal(input.amount, input.allocations);
      if (allocationError) {
        throw new Error(allocationError);
      }
      const checkNumberError = validateContributionCheckNumber(input.method, input.check_number);
      if (checkNumberError) {
        throw new Error(checkNumberError);
      }
      if (input.person_id) {
        const { data: person, error: personError } = await supabase
          .from("people")
          .select("id")
          .eq("id", input.person_id)
          .eq("organization_id", organizationId)
          .maybeSingle();

        if (personError) throw personError;
        if (!person) {
          throw new Error("Person not found for the current organization");
        }
      }

      const entry = {
        person_id: input.person_id,
        donor_name: input.donor_name,
        amount: input.amount,
        date: input.date,
        method: input.method,
        status: input.status || "pending",
        check_number: normalizeContributionCheckNumber(input.method, input.check_number),
        notes: input.notes,
        batch_id: input.batch_id ?? null,
        batch_date: input.batch_date ?? null,
        batch_source: input.batch_source ?? null,
        allocations: input.allocations,
      };
      const { data: createdRows, error: createError } = await supabase.rpc(
        "create_contribution_batch_with_auto_post_intent" as never,
        {
          p_organization_id: organizationId,
          p_entries: [entry],
          p_auto_post_to_gl: true,
        } as never,
      );
      if (createError) throw createError;
      if (!createdRows || !Array.isArray(createdRows)) {
        throw new Error("Contribution creation returned no rows");
      }
      const created = createdRows[0] as Contribution | undefined;
      if (!created) throw new Error("Contribution not created");

      // The database transaction is already committed. Telemetry cannot turn
      // that success into a rejected mutation, which would invite a retry and
      // duplicate the gift.
      await emitBatchContributionPaymentEventsBestEffort({
        organizationId,
        contributions: [created],
      });

      // Auto-post contribution to the General Ledger via the shared helper
      // (resolves the ready_for_export finance entry the trg_auto_create_finance_entry
      // trigger created, then posts it). Non-blocking: on failure the entry stays
      // ready_for_export and can be posted manually from the finance queue.
      // A staff-entered pending gift is not postable until reviewed: the posting
      // freshness guard rejects it and would stamp last_error on its entry. The
      // auto-post intent persisted above posts it when it is marked reviewed.
      if (isCountableGivingContribution(created.status, created.batch_source)) {
        try {
          await autoPostContributionsByContributionIds({
            organizationId,
            contributionIds: [created.id],
          });
        } catch (postError) {
          console.warn("Auto-post to GL failed (non-blocking):", postError);
        }
      }

      // Auto-send the donor receipt. A staff-entered pending gift is skipped
      // here and receives it at its review transition instead.
      await sendContributionReceiptBestEffort({
        organizationId,
        contribution: created,
        allocations: input.allocations,
      });

      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
      queryClient.invalidateQueries({ queryKey: ["forge", "pledges"] });
      queryClient.invalidateQueries({ queryKey: ["forge", "campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["pledges"] });
      queryClient.invalidateQueries({ queryKey: ["pledge"] });
      queryClient.invalidateQueries({ queryKey: ["pledge-matches"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
      // A single contribution can be created with batch_id/batch_date or a
      // processor batch_source, which PostingLog.tsx's Batches tab
      // summarizes -- see invalidateFinanceQueueQueries's own comment
      // (Codex P2, PR #6406, round 3).
      invalidateFinanceQueueQueries(queryClient);
    },
    onError: (error) => {
      toast.error("Failed to create contribution", {
        description: error.message,
      });
    },
  });
}

/** Either the bare inputs, or the inputs plus the batch's auto-post choice. */
export type CreateContributionBatchVariables =
  | CreateContributionInput[]
  | {
      inputs: CreateContributionInput[];
      autoPostToGL: boolean;
      /** Capture newly received interactive gifts; historical/import callers omit this. */
      provenance?: "interactive-new-batch";
      /** Local completion feedback only; the captured save session may have retired. */
      shouldNotify?: () => boolean;
    };

// Batch create contributions
export function useCreateContributionBatch() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (variables: CreateContributionBatchVariables) => {
      if (!organizationId) throw new Error("No organization selected");
      const inputs = Array.isArray(variables) ? variables : variables.inputs;
      const autoPostToGL = Array.isArray(variables) ? undefined : variables.autoPostToGL;

      // Validate every contribution + its allocations up-front so a sub-cent
      // mismatch doesn't get half-written via the non-transactional fallback.
      for (let i = 0; i < inputs.length; i += 1) {
        const input = inputs[i];
        if (!Number.isFinite(input.amount)) {
          throw new Error(`Contribution #${i + 1}: amount must be a finite number`);
        }
        const err = validateAllocationTotal(input.amount, input.allocations);
        if (err) {
          throw new Error(`Contribution #${i + 1}: ${err}`);
        }
        const checkNumberError = validateContributionCheckNumber(input.method, input.check_number);
        if (checkNumberError) {
          throw new Error(`Contribution #${i + 1}: ${checkNumberError}`);
        }
      }

      const transactionalPayload = inputs.map((input) => ({
        person_id: input.person_id,
        donor_name: input.donor_name,
        amount: input.amount,
        date: input.date,
        method: input.method,
        status: input.status || "pending",
        check_number: normalizeContributionCheckNumber(input.method, input.check_number),
        notes: input.notes,
        batch_id: input.batch_id,
        batch_date: input.batch_date,
        batch_source: input.batch_source,
        allocations: input.allocations,
      }));

      // RPC args can infer as never when client schema is dynamic; cast to satisfy type checker
      // When the caller states an auto-post choice, persist it in the SAME
      // transaction as the batch. A follow-up write could fail after the batch
      // committed and silently drop the user's accounting choice.
      const { data: transactionalResults, error: transactionalError } = autoPostToGL === undefined
        ? await supabase.rpc(
            "create_contribution_batch_transaction",
            { p_organization_id: organizationId, p_entries: transactionalPayload } as Parameters<
              typeof supabase.rpc<"create_contribution_batch_transaction">
            >[1],
          )
        : await supabase.rpc(
            "create_contribution_batch_with_auto_post_intent" as never,
            {
              p_organization_id: organizationId,
              p_entries: transactionalPayload,
              p_auto_post_to_gl: autoPostToGL,
            } as never,
          );

      if (transactionalError) throw transactionalError;
      if (!Array.isArray(transactionalResults)) {
        throw new Error("Contribution batch returned no rows");
      }

      const createdContributions = transactionalResults as Contribution[];
      await emitBatchContributionPaymentEventsBestEffort({
        organizationId,
        contributions: createdContributions,
        captureReceivedCashAndChecks: !Array.isArray(variables)
          && variables.provenance === "interactive-new-batch",
      });
      return createdContributions;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
      queryClient.invalidateQueries({ queryKey: ["forge", "pledges"] });
      queryClient.invalidateQueries({ queryKey: ["forge", "campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["pledges"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
      // This is the primary way a Sunday cash/check batch gets created --
      // the Batches tab's totals would otherwise stay stale for this
      // mutation's own staleTime window (Codex P2, PR #6406, round 3).
      invalidateFinanceQueueQueries(queryClient);
      if (!Array.isArray(variables) && variables.shouldNotify?.() === false) return;
      toast.success(`Saved ${data.length} contribution${data.length !== 1 ? "s" : ""}`);
    },
    onError: (error, variables) => {
      if (!Array.isArray(variables) && variables.shouldNotify?.() === false) return;
      toast.error("Failed to save batch", {
        description: error.message,
      });
    },
  });
}

const normalizedContributionStatus = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

const isProcessorAccountingRetry = (
  contribution: { status?: string | null; batch_source?: string | null },
  updates: Partial<Contribution>,
): boolean =>
  isPaymentProcessorContributionSource(contribution.batch_source) &&
  normalizedContributionStatus(contribution.status) === "error" &&
  normalizedContributionStatus(typeof updates.status === "string" ? updates.status : null) === "pending_sync" &&
  Object.keys(updates).every((field) => field === "status");

async function retryProcessorContributionAccountingSync(
  organizationId: string,
  contributionId: string,
): Promise<Contribution> {
  const { data, error } = await supabase.rpc(
    "retry_processor_contribution_accounting_sync" as never,
    {
      p_organization_id: organizationId,
      p_contribution_id: contributionId,
    } as never,
  );
  if (error) throw error;
  if (!data) throw new Error("Accounting sync retry returned no data");
  return data as Contribution;
}

export async function markStaffContributionReviewed(
  organizationId: string,
  contributionId: string,
): Promise<Contribution> {
  const { data, error } = await supabase.rpc(
    "mark_staff_contribution_reviewed" as never,
    { p_organization_id: organizationId, p_contribution_id: contributionId } as never,
  );
  if (error) throw error;
  if (!data) throw new Error("Contribution review returned no data");
  return data as Contribution;
}

export function useMarkStaffContributionReviewed() {
  const queryClient = useQueryClient();
  const { organization, organizationId } = useOrganization();
  const receiptOnReview = sendsReceiptOnReview(organization?.settings);
  return useMutation({
    mutationFn: async (contributionId: string) => {
      const currentOrganizationId = organizationId;
      if (!currentOrganizationId) throw new Error("No organization selected");
      const updated = await markStaffContributionReviewed(currentOrganizationId, contributionId);
      await emitContributionPaymentEventBestEffort({
        eventName: "finance.payment.captured",
        organizationId: currentOrganizationId,
        contributionId: updated.id,
        amount: updated.amount,
        method: updated.method,
        transactionId: updated.transaction_id,
      });
      if ((updated as Contribution & { auto_post_to_gl?: boolean }).auto_post_to_gl) {
        try {
          await autoPostContributionsByContributionIds({
            organizationId: currentOrganizationId,
            contributionIds: [updated.id],
          });
        } catch (postError) {
          console.warn("Auto-post reviewed contribution to GL failed (non-blocking):", postError);
        }
      }
      // A donor receipt at review only when the organization turned it on (#6479,
      // off unless set), and from here alone: the review action on the gift's page
      // sends none of its own. Unforced, so send-receipt claims the gift's
      // receipt_sent_at first (#6481) and a gift already receipted, at save before
      // receipts waited for review, gets no second one. Never fails the review.
      if (receiptOnReview) {
        await sendContributionReceiptBestEffort({
          organizationId: currentOrganizationId,
          contribution: updated,
          // send-receipt reads the gift's funds from the database; these only
          // label the client-built copy it ignores.
          allocations: [],
        });
      }
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
      queryClient.invalidateQueries({ queryKey: ["contribution"] });
      queryClient.invalidateQueries({ queryKey: ["pledges"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
      invalidateFinanceQueueQueries(queryClient);
    },
    // Read error.message directly, as the sibling hooks do. supabase-js errors
    // are plain objects, so an `instanceof Error` guard would drop the database's
    // message. This does not swallow: mutateAsync still rejects, and the page
    // action catches it.
    onError: (error) => {
      toast.error("Failed to mark contribution reviewed", {
        description: error.message,
      });
    },
  });
}

export async function markStaffContributionsReviewed(
  organizationId: string,
  contributionIds: string[],
): Promise<Contribution[]> {
  const uniqueContributionIds = Array.from(new Set(contributionIds));
  if (uniqueContributionIds.length === 0) {
    throw new Error("Select at least one contribution");
  }

  const { data, error } = await supabase.rpc(
    "bulk_mark_staff_contributions_reviewed",
    {
      p_organization_id: organizationId,
      p_contribution_ids: uniqueContributionIds,
    },
  );
  if (error) throw error;
  if (!Array.isArray(data)) throw new Error("Bulk contribution review returned no rows");
  return data as Contribution[];
}

export function useBulkMarkStaffContributionsReviewed() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (contributionIds: string[]) => {
      const currentOrganizationId = organizationId;
      if (!currentOrganizationId) throw new Error("No organization selected");

      const reviewed = await markStaffContributionsReviewed(
        currentOrganizationId,
        contributionIds,
      );

      const eventResults = await Promise.allSettled(
        reviewed.map((contribution) =>
          emitContributionPaymentEvent({
            eventName: "finance.payment.captured",
            organizationId: currentOrganizationId,
            contributionId: contribution.id,
            amount: contribution.amount,
            method: contribution.method,
            transactionId: contribution.transaction_id,
          })
        ),
      );
      const failedEvents = eventResults.filter(
        (result): result is PromiseRejectedResult => result.status === "rejected",
      );
      if (failedEvents.length > 0) {
        console.warn(
          `Bulk contribution review finance event emission failed for ${failedEvents.length} contribution${failedEvents.length === 1 ? "" : "s"} after the database write committed (non-blocking):`,
          failedEvents.map((result) => result.reason),
        );
      }

      const autoPostIds = reviewed
        .filter((contribution) =>
          Boolean((contribution as Contribution & { auto_post_to_gl?: boolean }).auto_post_to_gl)
        )
        .map((contribution) => contribution.id);
      if (autoPostIds.length > 0) {
        try {
          await autoPostContributionsByContributionIds({
            organizationId: currentOrganizationId,
            contributionIds: autoPostIds,
          });
        } catch (postError) {
          console.warn("Auto-post bulk-reviewed contributions to GL failed (non-blocking):", postError);
        }
      }

      // No donor receipts are sent here, even when the organization sends one at
      // review (#6479): send-receipt allows ten emails a minute, so a batch sent
      // from here would be refused past the tenth, and a reviewed gift is not
      // reviewed again. How bulk review should deliver them is #8406.
      return reviewed;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
      queryClient.invalidateQueries({ queryKey: ["contribution"] });
      queryClient.invalidateQueries({ queryKey: ["pledges"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
      invalidateFinanceQueueQueries(queryClient);
    },
    onError: (error) => {
      toast.error("Failed to mark contributions reviewed", {
        description: error.message,
      });
    },
  });
}

// Update contribution
export function useUpdateContribution() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Contribution> & { id: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      if (updates.amount !== undefined && !Number.isFinite(updates.amount)) {
        throw new Error("Contribution amount must be a finite number");
      }

      if (updates.person_id) {
        const { data: person, error: personError } = await supabase
          .from("people")
          .select("id")
          .eq("id", updates.person_id)
          .eq("organization_id", organizationId)
          .maybeSingle();

        if (personError) throw personError;
        if (!person) {
          throw new Error("Person not found for the current organization");
        }
      }

      const { data: existing, error: existingError } = await supabase
        .from("contributions")
        .select("id, organization_id, method, check_number, status, batch_source")
        .eq("id", id)
        .eq("organization_id", organizationId)
        .maybeSingle();

      if (existingError) throw existingError;
      if (!existing) {
        throw new Error("Contribution not found for the current organization");
      }

      const processorAccountingRetry = isProcessorAccountingRetry(existing, updates);
      if (isPaymentProcessorContributionSource(existing.batch_source)) {
        const processorOwnedFields = new Set([
          "organization_id",
          "person_id",
          "amount",
          "gross_amount",
          "fee_amount",
          "net_amount",
          "refunded_amount",
          "date",
          "method",
          "check_number",
          "status",
          "void_reason",
          "batch_source",
          "deposit_id",
          "recurring_gift_id",
          "transaction_id",
          "qbo_entity_type",
          "qbo_entity_id",
          "qbo_exported_at",
          "stripe_customer_id",
          "stripe_connect_account_id",
          "stripe_payment_intent_id",
          "stripe_charge_id",
          "stripe_balance_transaction_id",
          "stripe_refund_id",
          "stripe_dispute_id",
          "stripe_payout_id",
        ]);
        if (
          !processorAccountingRetry &&
          Object.keys(updates).some((field) => processorOwnedFields.has(field))
        ) {
          throw new Error("Processor-owned payment details cannot be edited");
        }
      }

      const nextMethod = typeof updates.method === "string" ? updates.method : existing.method;
      const nextCheckNumber = updates.check_number !== undefined
        ? updates.check_number
        : existing.check_number;
      const changesCheckFields = updates.method !== undefined || updates.check_number !== undefined;
      if (changesCheckFields) {
        const checkNumberError = validateContributionCheckNumber(nextMethod, nextCheckNumber);
        if (checkNumberError) {
          throw new Error(checkNumberError);
        }
      }

      const updatePayload = {
        ...updates,
        ...(changesCheckFields
          ? { check_number: normalizeContributionCheckNumber(nextMethod, nextCheckNumber) }
          : {}),
      };
      let updated: Contribution;
      if (processorAccountingRetry) {
        updated = await retryProcessorContributionAccountingSync(organizationId, id);
      } else {
        const { data, error } = await supabase.rpc(
          "update_staff_contribution" as never,
          {
            p_organization_id: organizationId,
            p_contribution_id: id,
            p_updates: updatePayload,
          } as never,
        );

        if (error) throw error;
        if (!data) throw new Error("Contribution update returned no data");
        updated = data as Contribution;
      }
      // Status-aware finance event emission. Previously this fired
      // `payment_intent.confirmed` on every edit (notes-only updates,
      // batch reassignment, etc.) which polluted the finance event stream
      // and silently swallowed transitions to void/refunded/failed status.
      // We only emit when the caller actually changed status, and map void-
      // family statuses to `payment.failed` so downstream consumers can
      // exclude them. Filed under the void-status filtering pattern.
      const VOID_STATUSES = new Set([
        "void",
        "voided",
        "refunded",
        "reversed",
        "canceled",
        "cancelled",
      ]);
      if (typeof updates.status === "string" && typeof updated.status === "string") {
        const nextStatus = updated.status.toLowerCase();
        let eventName:
          | "finance.payment.captured"
          | "finance.payment.settled"
          | "finance.payment.failed"
          | null = null;
        let failureReason: string | null = null;
        if (VOID_STATUSES.has(nextStatus)) {
          eventName = "finance.payment.failed";
          failureReason = `status:${updated.status}`;
        } else if (nextStatus === "failed" || nextStatus === "error") {
          eventName = "finance.payment.failed";
          failureReason = `status:${updated.status}`;
        } else if (nextStatus === "synced") {
          eventName = "finance.payment.settled";
        } else if (nextStatus === "completed" || nextStatus === "pending") {
          eventName = "finance.payment.captured";
        }
        if (eventName) {
          await emitContributionPaymentEventBestEffort({
            eventName,
            organizationId,
            contributionId: updated.id,
            amount: updated.amount,
            method: updated.method,
            transactionId: updated.transaction_id,
            failureReason,
          });
        }
      }
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
      // `["contribution"]` prefix covers both the legacy `["contribution", id]`
      // and the org-scoped `["contribution", organizationId, id]` cache keys.
      queryClient.invalidateQueries({ queryKey: ["contribution"] });
      queryClient.invalidateQueries({ queryKey: ["pledges"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
      // An edit here can change amount/date/status/batch_id -- any of which
      // the Batches tab's totals summarize (Codex P2, PR #6406, round 3).
      invalidateFinanceQueueQueries(queryClient);
      toast.success("Contribution updated");
    },
    onError: (error) => {
      toast.error("Failed to update contribution", {
        description: error.message,
      });
    },
  });
}

export function useReplaceContributionAllocations() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({
      contributionId,
      contributionAmount,
      contributionDate,
      contributionMethod,
      checkNumber,
      notes,
      allocations,
    }: {
      contributionId: string;
      contributionAmount: number;
      contributionDate: string;
      contributionMethod: string;
      checkNumber?: string | null;
      notes?: string | null;
      allocations: ContributionAllocationReplacement[];
    }) => {
      if (!organizationId) throw new Error("No organization selected");
      const validationError = validateContributionAllocationReplacement(
        contributionAmount,
        allocations,
      );
      if (validationError) throw new Error(validationError);
      const allocationPayload: Json = allocations.map(({ id, fund_id, amount }) => ({
        ...(id ? { id } : {}),
        fund_id,
        amount,
      }));

      const { data, error } = await supabase.rpc("replace_contribution_allocations", {
        p_organization_id: organizationId,
        p_contribution_id: contributionId,
        p_amount: contributionAmount,
        p_date: contributionDate,
        p_method: contributionMethod,
        p_check_number: checkNumber ?? null,
        p_notes: notes ?? null,
        p_allocations: allocationPayload,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
      queryClient.invalidateQueries({ queryKey: ["contribution"] });
      queryClient.invalidateQueries({ queryKey: ["fund"] });
      queryClient.invalidateQueries({ queryKey: ["funds"] });
      queryClient.invalidateQueries({ queryKey: ["finance"] });
      // This RPC can change amount, date, and method -- all three feed the
      // Posting Log's finance queue and its Batches tab (see
      // invalidateFinanceQueueQueries's own comment below), and none of the
      // keys above are query keys that queue/tab use (Codex P2, PR #6406).
      invalidateFinanceQueueQueries(queryClient);
      toast.success("Contribution allocations updated");
    },
    onError: (error) => {
      toast.error("Failed to update contribution allocations", {
        description: error instanceof Error ? error.message : "Try again.",
      });
    },
  });
}

// Delete contribution (soft-delete).
//
// Previously this performed a HARD `.delete()` — irreversible, no audit
// trail, and a real compliance risk: IRS Pub. 1771 / §170 require
// substantiation records to be retained for 7 years, and a hard-deleted
// donor record can't be recovered if the IRS audits the donor.
//
// The standard pattern across the project (see `docs/CLAUDE.md`
// "Migration 1D — Add soft-delete support") is `deleted_at timestamptz`
// + SELECT-policy filtering. The Tier-3 audits (#1598, #1599) already
// added `.is("deleted_at", null)` filters to every read site, so this
// hook can now switch to a soft-delete without orphaning the record
// in any view.
//
// Benefits:
//   1. IRS 7-year retention compliance.
//   2. Undo becomes a real operation — the toast can now restore.
//   3. Audit trail preserved (`deleted_at` + `updated_at` tell the story).
//   4. Allocations are NOT cascade-deleted; they stay linked to the
//      soft-deleted parent for retention/audit. Read sites that don't
//      filter by `deleted_at` would now see them — but every read site
//      was tightened in #1598/#1599 to filter explicitly.
// PostingLog.tsx wires delete into the same ready_for_export finance queue
// Post uses, so a soft-delete or its undo can change what that queue should
// show. Neither is a "contribution" query by key, so the invalidations below
// wouldn't otherwise touch it — least of all the undo path, which runs
// minutes later from a toast with no page-local invalidation of its own
// (Codex P2, PR #6389). The same page's Batches tab (useBatches()/
// useStripeBatches(), query keys "batches"/"stripe-batches") has the
// identical gap: deleting or restoring a batched or Stripe-sourced gift
// changes what those totals should show, but they're mounted queries with
// their own cache the delete mutation never touches (Codex P2, PR #6406).
function invalidateFinanceQueueQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ["finance-posting-groups"] });
  queryClient.invalidateQueries({ queryKey: ["finance-queue-ready"] });
  queryClient.invalidateQueries({ queryKey: ["finance-queue"] });
  queryClient.invalidateQueries({ queryKey: ["batches"] });
  // Gift corrections also change the separately cached report rows/totals.
  // Prefix invalidation covers old and new batches when a gift is reassigned.
  queryClient.invalidateQueries({ queryKey: ["batch-report"] });
  queryClient.invalidateQueries({ queryKey: ["stripe-batches"] });
}

export function useDeleteContribution() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!organizationId) throw new Error("No organization selected");
      // Snapshot the row pre-delete so the audit entry's `old_data` can
      // reconstruct the giving record from old_data alone (IRS 7-year
      // retention: the audit row must be a self-contained substantiation
      // record).
      const { data: existing, error: existingError } = await supabase
        .from("contributions")
        .select("*")
        .eq("id", id)
        .eq("organization_id", organizationId)
        .is("deleted_at", null) // refuse to re-delete an already-deleted record
        .maybeSingle();

      if (existingError) throw existingError;
      if (!existing) {
        throw new Error("Contribution not found for the current organization");
      }
      const previous = existing as unknown as Record<string, unknown>;
      if (
        !isSettledStripeContribution(
          typeof previous.status === "string" ? previous.status : null,
          typeof previous.batch_source === "string" ? previous.batch_source : null,
        )
      ) {
        throw new Error("A Stripe contribution cannot be deleted until the payment has settled");
      }

      // Soft-delete the contribution AND reverse its General Ledger postings
      // in one transaction.
      //
      // `set_contributions_deleted_at` only writes `contributions.deleted_at`;
      // it has never touched the finance tables. Every giving-side reader
      // filters `deleted_at IS NULL`, so the gift vanishes from totals and
      // statements immediately — but the journal entry
      // `finance_post_contributions` created for it stays posted, and the
      // trial balance, statement of activities, balance sheet, fund balances
      // and 990 export all keep counting it.
      //
      // This goes through a single RPC rather than a delete call plus a
      // reversal call because the two-call shape has a window in which the
      // contribution is hidden and the ledger is not reversed — precisely the
      // orphaned-ledger state being fixed — and a compensating step cannot
      // close it, because the compensation can fail too. The RPC also
      // delegates authorization to `set_contributions_deleted_at` itself, so
      // the delete authority cannot drift from the reversal's.
      //
      // The row remains in place for 7-year IRS retention + undo support below.
      const deletedAt = new Date().toISOString();
      await deleteContributionsWithReversal({
        organizationId,
        contributionIds: [id],
        deletedAt,
        reason: "Contribution deleted",
      });

      // Audit-trail entry. old_data captures the full pre-delete row so
      // the deletion is fully reversible from the audit log alone — see
      // `revertAuditEntry` in src/lib/audit/index.ts.
      await logAudit({
        tableName: "contributions",
        recordId: id,
        action: "soft_delete",
        organizationId,
        oldData: previous,
        newData: { ...previous, deleted_at: deletedAt },
      });
      return id;
    },
    onSuccess: (deletedId, _id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
      queryClient.invalidateQueries({ queryKey: ["pledges"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
      invalidateFinanceQueueQueries(queryClient);
      showUndoToast({
        message: "Contribution deleted",
        duration: 10_000,
        // Undo is now a real operation since the row still exists.
        onUndo: async () => {
          if (!organizationId) {
            toast.error("Cannot undo — no organization context.");
            return;
          }
          // Mirror of the delete: un-hide the contribution and re-instate its
          // ledger postings in one transaction. Restoring the gift without
          // re-instating the posting would leave the GL netted to zero while
          // every giving surface counts the gift again — the same
          // inconsistency as the original bug, inverted.
          try {
            await undeleteContributionsWithPostings({
              organizationId,
              contributionIds: [deletedId],
              reason: "Contribution delete undone",
            });
          } catch (restoreError) {
            toast.error(
              `Could not restore: ${
                restoreError instanceof Error ? restoreError.message : String(restoreError)
              }`,
            );
            return;
          }

          // Audit the restore. Pairs with the `soft_delete` row written
          // in mutationFn — together they reconstruct the full
          // delete→undo cycle for compliance.
          await logAudit({
            tableName: "contributions",
            recordId: deletedId,
            action: "soft_delete_undo",
            organizationId,
            newData: { deleted_at: null },
          });
          queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
          queryClient.invalidateQueries({ queryKey: ["pledges"] });
          queryClient.invalidateQueries({ queryKey: ["campaigns"] });
          queryClient.invalidateQueries({ queryKey: ["campaign"] });
          invalidateFinanceQueueQueries(queryClient);
          toast.success("Contribution restored.");
        },
      });
    },
    onError: (error) => {
      showErrorToast("Failed to delete contribution", error.message);
    },
  });
}

export function useDeleteContributions(options?: { requireUnposted?: boolean }) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  const requireUnposted = options?.requireUnposted ?? false;

  const invalidateContributionData = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
    queryClient.invalidateQueries({ queryKey: ["pledges"] });
    queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    queryClient.invalidateQueries({ queryKey: ["campaign"] });
    invalidateFinanceQueueQueries(queryClient);
  };

  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!organizationId) throw new Error("No organization selected");
      const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
      if (uniqueIds.length === 0) return { deletedIds: [] as string[], skippedPostedIds: [] as string[] };

      const deletedAt = new Date().toISOString();
      const existingRows: Array<Record<string, unknown> & { id: string }> = [];

      // Read every selected row before mutating any of them. A prior
      // read/delete-per-chunk loop could commit the first 100 deletions before
      // discovering an unsettled Stripe contribution in a later chunk.
      for (let index = 0; index < uniqueIds.length; index += CONTRIBUTION_BULK_DELETE_CHUNK_SIZE) {
        const chunk = uniqueIds.slice(index, index + CONTRIBUTION_BULK_DELETE_CHUNK_SIZE);
        const { data: existing, error: existingError } = await supabase
          .from("contributions")
          .select("*")
          .eq("organization_id", organizationId)
          .in("id", chunk)
          .is("deleted_at", null);

        if (existingError) throw existingError;
        existingRows.push(
          ...((existing ?? []) as Array<Record<string, unknown> & { id: string }>),
        );
      }

      if (existingRows.length === 0) {
        throw new Error("No active contributions were found to delete.");
      }

      const hasUnsettledStripeContribution = existingRows.some((row) =>
        !isSettledStripeContribution(
          typeof row.status === "string" ? row.status : null,
          typeof row.batch_source === "string" ? row.batch_source : null,
        ),
      );
      if (hasUnsettledStripeContribution) {
        throw new Error("A selected Stripe contribution has not settled and cannot be deleted");
      }

      // Send the complete preflighted selection through one database
      // transaction. The database trigger rechecks settlement while locking
      // each row, so a concurrent lifecycle change rolls back the entire bulk
      // operation rather than leaving an earlier client chunk committed.
      const deletion = await deleteContributionsWithReversal({
        organizationId,
        contributionIds: existingRows.map((row) => row.id),
        deletedAt,
        reason: "Contribution deleted (bulk)",
        requireUnposted,
      });
      const deletedIds = deletion.deletedIds;
      const skippedPostedIds = deletion.skippedPostedIds;

      // Only an error when NOTHING happened. With requireUnposted, every
      // selected row turning out already-posted (skippedPostedIds non-empty,
      // deletedIds empty) is a valid outcome, not a failure -- the caller
      // reads skippedPostedIds to tell the user why.
      if (deletedIds.length === 0 && skippedPostedIds.length === 0) {
        throw new Error("No active contributions were found to delete.");
      }

      const existingById = new Map(existingRows.map((row) => [row.id, row]));
      for (let index = 0; index < deletedIds.length; index += CONTRIBUTION_BULK_DELETE_CHUNK_SIZE) {
        const auditIds = deletedIds.slice(index, index + CONTRIBUTION_BULK_DELETE_CHUNK_SIZE);
        await Promise.all(
          auditIds.map((id) => {
            const row = existingById.get(id);
            if (!row) return Promise.resolve();
            return logAudit({
              tableName: "contributions",
              recordId: row.id,
              action: "soft_delete",
              organizationId,
              oldData: row,
              newData: { ...row, deleted_at: deletedAt },
            });
          }),
        );
      }

      return { deletedIds, skippedPostedIds };
    },
    onSuccess: ({ deletedIds, skippedPostedIds }) => {
      invalidateContributionData();
      if (deletedIds.length === 0) {
        // Everything selected was already posted and skipped (requireUnposted)
        // -- nothing was deleted, so there is nothing to show an undo toast
        // for. The caller reads skippedPostedIds off the resolved mutation
        // to tell the user why.
        return;
      }
      const count = deletedIds.length;
      showUndoToast({
        message: `${count} contribution${count === 1 ? "" : "s"} deleted`,
        description: "Deleted contributions are hidden from totals but retained for audit history.",
        duration: 10_000,
        onUndo: async () => {
          if (!organizationId) {
            toast.error("Cannot undo — no organization context.");
            return;
          }

          // Restore the complete selection in one database transaction. Audit
          // writes may be chunked after the authoritative mutation, but the
          // contribution and ledger state must never be partially restored.
          try {
            await undeleteContributionsWithPostings({
              organizationId,
              contributionIds: deletedIds,
              reason: "Contribution delete undone (bulk)",
            });
          } catch (restoreError) {
            toast.error(
              `Could not restore: ${
                restoreError instanceof Error ? restoreError.message : String(restoreError)
              }`,
            );
            return;
          }

          for (let index = 0; index < deletedIds.length; index += CONTRIBUTION_BULK_DELETE_CHUNK_SIZE) {
            const chunk = deletedIds.slice(index, index + CONTRIBUTION_BULK_DELETE_CHUNK_SIZE);
            await Promise.all(
              chunk.map((id) =>
                logAudit({
                  tableName: "contributions",
                  recordId: id,
                  action: "soft_delete_undo",
                  organizationId,
                  newData: { deleted_at: null },
                })
              )
            );
          }

          invalidateContributionData();
          toast.success(`${count} contribution${count === 1 ? "" : "s"} restored.`);
        },
      });
    },
    onError: (error) => {
      showErrorToast("Failed to delete selected contributions", error.message);
    },
  });
}

// Update contribution status with undo
export function useUpdateContributionStatus() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      // Get current status for undo
      const { data: current, error: currentError } = await supabase
        .from("contributions")
        .select("status, batch_source")
        .eq("id", id)
        .eq("organization_id", organizationId)
        .maybeSingle();

      if (currentError) throw currentError;
      if (!current) {
        throw new Error("Contribution not found for the current organization");
      }
      const processorAccountingRetry = isProcessorAccountingRetry(current, { status });
      if (
        isPaymentProcessorContributionSource(current.batch_source) &&
        !processorAccountingRetry
      ) {
        throw new Error("Processor contribution status is updated from provider events");
      }

      let updated: Contribution;
      if (processorAccountingRetry) {
        updated = await retryProcessorContributionAccountingSync(organizationId, id);
      } else {
        const { data, error } = await supabase.rpc(
          "update_staff_contribution" as never,
          {
            p_organization_id: organizationId,
            p_contribution_id: id,
            p_updates: { status },
          } as never,
        );

        if (error) throw error;
        if (!data) throw new Error("Contribution update returned no data");
        updated = data as Contribution;
      }

      // Status-to-event mapping. Lowercase the lookup so the DB-level
      // status (which is a free `text` column with a CHECK constraint, not a
      // typed enum at the Postgres layer) is matched case-insensitively.
      // Void-family statuses MUST emit `payment.failed` so downstream finance
      // consumers (GL posting, giving statements) exclude them. Without this,
      // a contribution voided via this hook stays in the "active" event
      // stream and inflates IRS-significant totals (see PR #1522/#1538).
      const statusToEvent: Record<string, "finance.payment.captured" | "finance.payment.settled" | "finance.payment.failed"> = {
        pending: "finance.payment.captured",
        completed: "finance.payment.captured",
        synced: "finance.payment.settled",
        failed: "finance.payment.failed",
        error: "finance.payment.failed",
        void: "finance.payment.failed",
        voided: "finance.payment.failed",
        refunded: "finance.payment.failed",
        reversed: "finance.payment.failed",
        canceled: "finance.payment.failed",
        cancelled: "finance.payment.failed",
      };
      const normalizedStatus = (status ?? "").toLowerCase();
      const mappedEvent = statusToEvent[normalizedStatus];
      if (mappedEvent) {
        await emitContributionPaymentEventBestEffort({
          eventName: mappedEvent,
          organizationId,
          contributionId: updated.id,
          amount: updated.amount,
          method: updated.method,
          transactionId: updated.transaction_id,
          failureReason: mappedEvent === "finance.payment.failed" ? `status:${status}` : null,
        });
      }
      const prevStatus = current as { status: string } | null;
      return {
        contribution: updated,
        // A processor retry is a queued accounting operation, so do not offer
        // the generic status undo that would try to write provider lifecycle
        // state through the ordinary staff-edit boundary.
        previousStatus: processorAccountingRetry ? undefined : prevStatus?.status,
      };
    },
    onSuccess: ({ contribution, previousStatus }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
      // Prefix invalidation covers the org-scoped `["contribution", orgId, id]` key.
      queryClient.invalidateQueries({ queryKey: ["contribution"] });
      queryClient.invalidateQueries({ queryKey: ["pledges"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
      // A status change (e.g. into or out of a countable status) directly
      // changes what the Batches tab's totals count (Codex P2, PR #6406,
      // round 3).
      invalidateFinanceQueueQueries(queryClient);

      if (previousStatus) {
        showUndoToast({
          message: `Status updated to ${contribution.status}`,
          onUndo: async () => {
            const { error: undoError } = await supabase.rpc(
              "update_staff_contribution" as never,
              {
                p_organization_id: organizationId,
                p_contribution_id: contribution.id,
                p_updates: { status: previousStatus },
              } as never,
            );
            if (undoError) {
              toast.error("Failed to revert status", {
                description: undoError.message,
              });
              return;
            }
            // Audit the reverted-status write so that downstream
            // finance-event stream consumers see "status changed back"
            // as a first-class event, not a silent rewind. Logged only after
            // the revert actually persisted, so a rejected undo leaves no
            // false status_undo row now that log_audit_event() lands writes.
            if (organizationId) {
              await logAudit({
                tableName: "contributions",
                recordId: contribution.id,
                action: "status_undo",
                organizationId,
                oldData: { status: contribution.status },
                newData: { status: previousStatus },
              });
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.contributions.all });
            invalidateFinanceQueueQueries(queryClient);
            showSuccessToast("Status reverted");
          },
        });
      } else {
        showSuccessToast(`Status updated to ${contribution.status}`);
      }
    },
    onError: (error) => {
      toast.error("Failed to update status", {
        description: error.message,
      });
    },
  });
}
